import { isExplorerId, type ExplorerId } from "./explorers";

export const SAVE_VERSION = 7 as const;
export const GAME_VERSION = "0.12.0";

export type EvidenceLevel = "introduced" | "supported" | "independent";
export type RewardKind = "badge" | "cosmetic" | "sanctuary";
export type GameRegion =
  | "sanctuary"
  | "skeleton-safari"
  | "muscle-meadow"
  | "classification-coast"
  | "healing-habitat";
export type ChallengeKind =
  | "assembly"
  | "scenario"
  | "sequence"
  | "matching"
  | "comparison"
  | "mechanism"
  | "planning";

export interface LearningObjective {
  id: string;
  title: string;
  sourceSections: string[];
}

export interface DialogueBeat {
  id: string;
  speaker: string;
  text: string;
}

export interface RewardDefinition {
  id: string;
  kind: RewardKind;
  title: string;
}

export interface QuestDefinition {
  id: string;
  title: string;
  objectiveIds: string[];
  dialogue: DialogueBeat[];
  rewardIds: string[];
}

export interface ChallengeDefinition {
  id: string;
  stationId: string;
  title: string;
  kind: ChallengeKind;
  objectiveIds: string[];
  hints: [string, string];
  successExplanation: string;
}

export interface ChallengeAttempt {
  challengeId: string;
  objectiveId: string;
  evidence: EvidenceLevel;
  hintsUsed: number;
  completedAt: string;
}

export interface ObjectiveProgress {
  strongestEvidence: EvidenceLevel;
  attempts: number;
  lastAttemptAt: string;
}

export interface PlayerProgress {
  completedQuestIds: string[];
  completedChallengeIds: string[];
  unlockedRewardIds: string[];
  objectiveEvidence: Record<string, ObjectiveProgress>;
  attempts: ChallengeAttempt[];
}

export interface PlayerPosition {
  x: number;
  y: number;
  z: number;
}

export type CompanionStyle = "leaf" | "sunset" | "ocean" | "starlight";

export interface PlayerSettings {
  reducedMotion: boolean;
  textScale: "normal" | "large";
  readAloud: boolean;
  companionStyle: CompanionStyle;
  explorerId: ExplorerId;
}

interface SaveGameBase {
  gameVersion: string;
  updatedAt: string;
  player: { position: PlayerPosition };
}

export interface SaveGameV1 extends SaveGameBase {
  version: 1;
  progress: Omit<PlayerProgress, "completedChallengeIds">;
  settings: Omit<PlayerSettings, "readAloud" | "companionStyle" | "explorerId">;
}

export interface SaveGameV2 extends SaveGameBase {
  version: 2;
  region: "sanctuary" | "skeleton-safari";
  progress: PlayerProgress;
  settings: Omit<PlayerSettings, "companionStyle" | "explorerId">;
}

export interface SaveGameV3 extends SaveGameBase {
  version: 3;
  region: "sanctuary" | "skeleton-safari" | "muscle-meadow";
  progress: PlayerProgress;
  settings: Omit<PlayerSettings, "companionStyle" | "explorerId">;
}

export interface SaveGameV4 extends SaveGameBase {
  version: 4;
  region:
    "sanctuary" | "skeleton-safari" | "muscle-meadow" | "classification-coast";
  progress: PlayerProgress;
  settings: Omit<PlayerSettings, "companionStyle" | "explorerId">;
}

export interface SaveGameV5 extends SaveGameBase {
  version: 5;
  region: GameRegion;
  progress: PlayerProgress;
  settings: Omit<PlayerSettings, "companionStyle" | "explorerId">;
}

export interface SaveGameV6 extends SaveGameBase {
  version: 6;
  region: GameRegion;
  progress: PlayerProgress;
  settings: Omit<PlayerSettings, "explorerId">;
}

export interface SaveGameV7 extends SaveGameBase {
  version: typeof SAVE_VERSION;
  region: GameRegion;
  progress: PlayerProgress;
  settings: PlayerSettings;
}

export type SaveGame = SaveGameV7;

export function createInitialSave(
  updatedAt = new Date().toISOString(),
): SaveGame {
  return {
    version: SAVE_VERSION,
    gameVersion: GAME_VERSION,
    updatedAt,
    region: "sanctuary",
    player: { position: { x: 0, y: 0.05, z: 5 } },
    progress: {
      completedQuestIds: [],
      completedChallengeIds: [],
      unlockedRewardIds: [],
      objectiveEvidence: {},
      attempts: [],
    },
    settings: {
      reducedMotion: false,
      textScale: "normal",
      readAloud: false,
      companionStyle: "leaf",
      explorerId: "lily",
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOnlyStrings(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function isFinitePosition(value: unknown): value is PlayerPosition {
  if (!isRecord(value)) return false;
  return [value.x, value.y, value.z].every(
    (coordinate) =>
      typeof coordinate === "number" && Number.isFinite(coordinate),
  );
}

function isEvidenceLevel(value: unknown): value is EvidenceLevel {
  return ["introduced", "supported", "independent"].includes(String(value));
}

function isAttempt(value: unknown): value is ChallengeAttempt {
  if (!isRecord(value)) return false;
  return (
    typeof value.challengeId === "string" &&
    typeof value.objectiveId === "string" &&
    isEvidenceLevel(value.evidence) &&
    typeof value.hintsUsed === "number" &&
    Number.isInteger(value.hintsUsed) &&
    value.hintsUsed >= 0 &&
    typeof value.completedAt === "string"
  );
}

function isObjectiveProgress(value: unknown): value is ObjectiveProgress {
  if (!isRecord(value)) return false;
  return (
    isEvidenceLevel(value.strongestEvidence) &&
    typeof value.attempts === "number" &&
    Number.isInteger(value.attempts) &&
    value.attempts >= 0 &&
    typeof value.lastAttemptAt === "string"
  );
}

function hasValidBase(value: Record<string, unknown>): boolean {
  return (
    typeof value.gameVersion === "string" &&
    typeof value.updatedAt === "string" &&
    isRecord(value.player) &&
    isFinitePosition(value.player.position) &&
    isRecord(value.progress) &&
    isRecord(value.settings)
  );
}

function hasValidProgress(
  progress: Record<string, unknown>,
  requireChallenges: boolean,
): boolean {
  return (
    hasOnlyStrings(progress.completedQuestIds) &&
    (!requireChallenges || hasOnlyStrings(progress.completedChallengeIds)) &&
    hasOnlyStrings(progress.unlockedRewardIds) &&
    isRecord(progress.objectiveEvidence) &&
    Object.values(progress.objectiveEvidence).every(isObjectiveProgress) &&
    Array.isArray(progress.attempts) &&
    progress.attempts.every(isAttempt)
  );
}

function hasLegacySettings(settings: Record<string, unknown>): boolean {
  return (
    typeof settings.reducedMotion === "boolean" &&
    ["normal", "large"].includes(String(settings.textScale)) &&
    typeof settings.readAloud === "boolean"
  );
}

function hasVersion6Settings(settings: Record<string, unknown>): boolean {
  return (
    hasLegacySettings(settings) &&
    ["leaf", "sunset", "ocean", "starlight"].includes(
      String(settings.companionStyle),
    )
  );
}

function hasValidSettings(settings: Record<string, unknown>): boolean {
  return hasVersion6Settings(settings) && isExplorerId(settings.explorerId);
}

function isSaveGameV1(value: unknown): value is SaveGameV1 {
  if (!isRecord(value) || value.version !== 1 || !hasValidBase(value))
    return false;
  const progress = value.progress as Record<string, unknown>;
  const settings = value.settings as Record<string, unknown>;
  return (
    hasValidProgress(progress, false) &&
    typeof settings.reducedMotion === "boolean" &&
    ["normal", "large"].includes(String(settings.textScale))
  );
}

function isSaveGameV2(value: unknown): value is SaveGameV2 {
  if (!isRecord(value) || value.version !== 2 || !hasValidBase(value))
    return false;
  return (
    ["sanctuary", "skeleton-safari"].includes(String(value.region)) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasLegacySettings(value.settings as Record<string, unknown>)
  );
}

function isSaveGameV3(value: unknown): value is SaveGameV3 {
  if (!isRecord(value) || value.version !== 3 || !hasValidBase(value))
    return false;
  return (
    ["sanctuary", "skeleton-safari", "muscle-meadow"].includes(
      String(value.region),
    ) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasLegacySettings(value.settings as Record<string, unknown>)
  );
}

function isSaveGameV4(value: unknown): value is SaveGameV4 {
  if (!isRecord(value) || value.version !== 4 || !hasValidBase(value))
    return false;
  return (
    [
      "sanctuary",
      "skeleton-safari",
      "muscle-meadow",
      "classification-coast",
    ].includes(String(value.region)) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasLegacySettings(value.settings as Record<string, unknown>)
  );
}

function isSaveGameV5(value: unknown): value is SaveGameV5 {
  if (!isRecord(value) || value.version !== 5 || !hasValidBase(value))
    return false;
  return (
    [
      "sanctuary",
      "skeleton-safari",
      "muscle-meadow",
      "classification-coast",
      "healing-habitat",
    ].includes(String(value.region)) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasLegacySettings(value.settings as Record<string, unknown>)
  );
}

function isSaveGameV6(value: unknown): value is SaveGameV6 {
  if (!isRecord(value) || value.version !== 6 || !hasValidBase(value))
    return false;
  return (
    isGameRegion(value.region) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasVersion6Settings(value.settings as Record<string, unknown>)
  );
}

function isGameRegion(value: unknown): value is GameRegion {
  return [
    "sanctuary",
    "skeleton-safari",
    "muscle-meadow",
    "classification-coast",
    "healing-habitat",
  ].includes(String(value));
}

export function isSaveGame(value: unknown): value is SaveGame {
  if (
    !isRecord(value) ||
    value.version !== SAVE_VERSION ||
    !hasValidBase(value)
  ) {
    return false;
  }
  return (
    isGameRegion(value.region) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasValidSettings(value.settings as Record<string, unknown>)
  );
}

export function migrateSaveGame(value: unknown): SaveGame | undefined {
  if (isSaveGame(value)) return value;
  if (
    isRecord(value) &&
    value.version === SAVE_VERSION &&
    hasValidBase(value) &&
    isGameRegion(value.region) &&
    hasValidProgress(value.progress as Record<string, unknown>, true) &&
    hasVersion6Settings(value.settings as Record<string, unknown>)
  ) {
    return {
      ...(value as unknown as SaveGame),
      gameVersion: GAME_VERSION,
      settings: {
        ...(value.settings as Omit<PlayerSettings, "explorerId">),
        explorerId: "lily",
      },
    };
  }
  if (isSaveGameV6(value)) {
    return {
      ...value,
      version: SAVE_VERSION,
      gameVersion: GAME_VERSION,
      settings: { ...value.settings, explorerId: "lily" },
    };
  }
  if (
    isSaveGameV5(value) ||
    isSaveGameV4(value) ||
    isSaveGameV3(value) ||
    isSaveGameV2(value)
  ) {
    return {
      ...value,
      version: SAVE_VERSION,
      gameVersion: GAME_VERSION,
      settings: {
        ...value.settings,
        companionStyle: "leaf",
        explorerId: "lily",
      },
    };
  }
  if (!isSaveGameV1(value)) return undefined;
  return {
    ...value,
    version: SAVE_VERSION,
    gameVersion: GAME_VERSION,
    region: "sanctuary",
    progress: { ...value.progress, completedChallengeIds: [] },
    settings: {
      ...value.settings,
      readAloud: false,
      companionStyle: "leaf",
      explorerId: "lily",
    },
  };
}

export function parseSaveGame(serialized: string): SaveGame | undefined {
  try {
    return migrateSaveGame(JSON.parse(serialized) as unknown);
  } catch {
    return undefined;
  }
}
